
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const units = [
  { id: 'l100km', name: 'Liters per 100km (L/100km)' },
  { id: 'mpg_us', name: 'Miles per gallon (US)' },
  { id: 'mpg_imp', name: 'Miles per gallon (UK)' },
  { id: 'kml', name: 'Kilometers per liter (km/L)' },
  { id: 'mpl_us', name: 'Miles per liter (US)' },
  { id: 'mpl_imp', name: 'Miles per liter (UK)' },
  { id: 'gp100m_us', name: 'Gallons per 100 miles (US)' },
  { id: 'gp100m_imp', name: 'Gallons per 100 miles (UK)' },
];

// Base unit is L/100km (liters per 100 km)
const FuelConsumptionConverter = () => {
  const [fromValue, setFromValue] = useState<string>('1');
  const [fromUnit, setFromUnit] = useState<string>('l100km');
  const [toUnit, setToUnit] = useState<string>('mpg_us');
  const [toValue, setToValue] = useState<string>('');

  useEffect(() => {
    convert();
  }, [fromValue, fromUnit, toUnit]);

  const convert = () => {
    try {
      const inputValue = parseFloat(fromValue);
      if (!isNaN(inputValue) && inputValue !== 0) {
        let baseValue: number; // L/100km
        let result: number;

        // Convert from input unit to L/100km
        switch(fromUnit) {
          case 'l100km':
            baseValue = inputValue;
            break;
          case 'mpg_us':
            baseValue = 235.214583 / inputValue;
            break;
          case 'mpg_imp':
            baseValue = 282.48 / inputValue;
            break;
          case 'kml':
            baseValue = 100 / inputValue;
            break;
          case 'mpl_us':
            baseValue = 62.14 / inputValue;
            break;
          case 'mpl_imp':
            baseValue = 62.14 / inputValue;
            break;
          case 'gp100m_us':
            baseValue = inputValue * 2.35214583;
            break;
          case 'gp100m_imp':
            baseValue = inputValue * 2.8248;
            break;
          default:
            baseValue = inputValue;
        }

        // Convert from L/100km to output unit
        switch(toUnit) {
          case 'l100km':
            result = baseValue;
            break;
          case 'mpg_us':
            result = 235.214583 / baseValue;
            break;
          case 'mpg_imp':
            result = 282.48 / baseValue;
            break;
          case 'kml':
            result = 100 / baseValue;
            break;
          case 'mpl_us':
            result = 62.14 / baseValue;
            break;
          case 'mpl_imp':
            result = 62.14 / baseValue;
            break;
          case 'gp100m_us':
            result = baseValue / 2.35214583;
            break;
          case 'gp100m_imp':
            result = baseValue / 2.8248;
            break;
          default:
            result = baseValue;
        }

        setToValue(result.toFixed(6).replace(/\.?0+$/, ""));
      } else {
        setToValue('');
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setToValue('Error');
    }
  };

  const handleFromValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromValue(e.target.value);
  };

  const handleFromUnitChange = (value: string) => {
    setFromUnit(value);
  };

  const handleToUnitChange = (value: string) => {
    setToUnit(value);
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Fuel Consumption Converter</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">From:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="number" 
                value={fromValue} 
                onChange={handleFromValueChange} 
                className="col-span-2"
              />
              <Select value={fromUnit} onValueChange={handleFromUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-center">
            <button 
              onClick={swapUnits}
              className="p-2 rounded-full hover:bg-muted"
            >
              ⇅
            </button>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">To:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="text" 
                value={toValue} 
                readOnly 
                className="col-span-2"
              />
              <Select value={toUnit} onValueChange={handleToUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FuelConsumptionConverter;
